/**
 * Opportunity Map Screen
 * 
 * Focused map for a single opportunity showing parcel boundaries,
 * neighbor tiers, and geo-pinned media (photos, voice notes, field notes).
 * Toggle layers, click pins to see popups and take actions.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, Platform, Pressable, ActivityIndicator, Alert } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, space, text, icon, radius } from '../../../shared/tokens/mobile';
import { ScreenContainer, ScreenHeader } from '../../../components/v2/mobile';
import { buildOpportunityMapHtml } from '../../../components/v2/map/opportunityMapHtml';
import { BASEMAP_STYLES } from '../../../components/v2/map/mapHtmlBuilder';
import { authApi } from '../../../shared/utils/api';
import { logger } from '../../../shared/utils/logger';
import { useMapData } from '../../../hooks/useMapData';

type BasemapKey = 'outdoors' | 'satellite-streets';
const BASEMAPS: { key: BasemapKey; label: string }[] = [
  { key: 'satellite-streets', label: 'Satellite' },
  { key: 'outdoors', label: 'Terrain' },
];

export default function OpportunityMapScreen() {
  const router = useRouter();
  const { portfolioId, opportunityId, opportunityName } = useLocalSearchParams<{
    portfolioId: string; opportunityId: string; opportunityName?: string;
  }>();
  const portId = parseInt(portfolioId || '0', 10);
  const oppId = parseInt(opportunityId || '0', 10);
  const { mapboxToken } = useMapData();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [basemap, setBasemap] = useState<BasemapKey>('satellite-streets');

  // Media data for popups
  const photosRef = useRef<any[]>([]);
  const voiceRef = useRef<any[]>([]);
  const notesRef = useRef<any[]>([]);
  const splitPartsRef = useRef<any[]>([]);
  const splitOriginalRef = useRef<number | null>(null);

  const mapHtml = useMemo(() => {
    if (!mapboxToken) return '';
    return buildOpportunityMapHtml({
      mapboxToken,
      center: [-79.5, 36.4],
      zoom: 14,
      basemapStyle: BASEMAP_STYLES[basemap],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapboxToken]);

  const postMsg = useCallback((msg: object) => {
    if (Platform.OS === 'web' && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(msg, '*');
    }
  }, []);

  // Load all data and send to map
  const loadData = useCallback(async () => {
    if (!oppId || !portId) return;
    setDataLoading(true);
    try {
      const [featResp, photoResp, voiceResp, noteResp] = await Promise.all([
        authApi.getPropertyFeatures(oppId, { include_neighbors: true, max_tier: 3 }),
        authApi.getOpportunityPhotos(portId, oppId),
        authApi.getVoiceNotes(portId, oppId),
        authApi.getFieldNotes(portId, oppId),
      ]);

      photosRef.current = photoResp.success ? photoResp.photos : [];
      voiceRef.current = voiceResp.success ? voiceResp.voiceNotes : [];
      notesRef.current = noteResp.success ? noteResp.fieldNotes : [];

      if (featResp.success && featResp.features) {
        const parseNum = (val: any): number | null => {
          if (val == null) return null;
          const num = typeof val === 'string' ? parseFloat(val) : val;
          return isNaN(num) ? null : num;
        };
        const extractParcel = (f: any) => ({
          id: f.id || f.feature_id,
          name: f.parcel_id || f.external_id || '',
          geometry: typeof f.geometry_geojson === 'string' ? JSON.parse(f.geometry_geojson) : f.geometry_geojson,
          acreage: parseNum(f.area_acres) ?? parseNum(f.acreage),
        });

        const primary = featResp.features
          .filter((f: any) => f.layer_code === 'parcel' && f.association_type === 'primary' && f.geometry_geojson)
          .map(extractParcel);

        const neighborsByTier: Record<number, any[]> = {};
        if (featResp.neighborsByTier && featResp.neighborsByTier.length > 0) {
          (featResp.neighborsByTier as any[]).forEach((tier: any) => {
            neighborsByTier[tier.tier] = tier.neighbors
              .filter((n: any) => n.geometry_geojson)
              .map((n: any) => ({
                id: n.id,
                name: n.parcel_id || '',
                geometry: typeof n.geometry_geojson === 'string' ? JSON.parse(n.geometry_geojson) : n.geometry_geojson,
                acreage: parseNum(n.area_acres),
              }));
          });
        } else {
          featResp.features
            .filter((f: any) => f.layer_code === 'parcel' && f.association_type !== 'primary' && f.geometry_geojson && f.neighbor_tier)
            .forEach((f: any) => {
              const tier = f.neighbor_tier || 1;
              if (!neighborsByTier[tier]) neighborsByTier[tier] = [];
              neighborsByTier[tier].push(extractParcel(f));
            });
        }

        postMsg({ type: 'set_parcels', parcels: primary, neighborsByTier });
      }

      postMsg({
        type: 'set_media_pins',
        pins: {
          photos: photosRef.current,
          voiceNotes: voiceRef.current,
          fieldNotes: notesRef.current,
        },
      });
    } catch (err) {
      logger.error('Failed to load opportunity map data', err, 'opp-map');
    } finally {
      setDataLoading(false);
    }
  }, [oppId, portId, postMsg]);

  useEffect(() => {
    if (mapReady) loadData();
  }, [mapReady, loadData]);

  // Handle messages from iframe
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handler = (e: MessageEvent) => {
      const data = e.data;
      if (!data?.type) return;
      if (data.type === 'map_ready') setMapReady(true);
      if (data.type === 'media_pin_action') handlePinAction(data.action, data.mediaType, data.id);
      if (data.type === 'split_request') handleSplitRequest(data.featureId, data.splitLine);
      if (data.type === 'split_confirm') handleSplitConfirm(data.partIndex);
      if (data.type === 'merge_request') handleMergeRequest(data.featureIds);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const handlePinAction = useCallback(async (action: string, mediaType: string, id: number) => {
    if (action === 'delete') {
      const confirmed = window.confirm(`Delete this ${mediaType === 'photo' ? 'photo' : mediaType === 'voice' ? 'voice note' : 'field note'}?`);
      if (!confirmed) return;
      try {
        if (mediaType === 'photo') await authApi.deleteOpportunityPhoto(portId, oppId, id);
        else if (mediaType === 'voice') await authApi.deleteVoiceNote(portId, oppId, id);
        else await authApi.deleteFieldNote(portId, oppId, id);
        loadData();
      } catch (err) {
        logger.error('Failed to delete media', err, 'opp-map');
      }
    } else if (action === 'view' && mediaType === 'photo') {
      try {
        const resp = await authApi.getOpportunityPhoto(portId, oppId, id);
        if (resp.success && resp.photo.image_data) {
          window.open(`data:${resp.photo.mime_type};base64,${resp.photo.image_data}`, '_blank');
        }
      } catch (err) {
        logger.error('Failed to open photo', err, 'opp-map');
      }
    } else if (action === 'play' && mediaType === 'voice') {
      try {
        const resp = await authApi.getVoiceNote(portId, oppId, id);
        if (resp.success && resp.voiceNote.audio_data) {
          const audio = new Audio(`data:${resp.voiceNote.mime_type || 'audio/m4a'};base64,${resp.voiceNote.audio_data}`);
          audio.play();
        }
      } catch (err) {
        logger.error('Failed to play voice note', err, 'opp-map');
      }
    }
  }, [portId, oppId, loadData]);

  const handleSplitRequest = useCallback(async (featureId: number, splitLine: object) => {
    try {
      const result = await authApi.splitParcel(featureId, splitLine);
      if (result.success) {
        splitPartsRef.current = result.parts;
        splitOriginalRef.current = result.originalFeatureId;
        postMsg({ type: 'split_result', success: true, parts: result.parts });
      } else {
        postMsg({ type: 'split_result', success: false, error: 'Split failed' });
      }
    } catch (err: any) {
      const msg = err?.message || 'Split failed';
      postMsg({ type: 'split_result', success: false, error: msg });
      logger.error('Split parcel failed', err, 'opp-map');
    }
  }, [postMsg]);

  const handleSplitConfirm = useCallback(async (partIndex: number) => {
    const part = splitPartsRef.current[partIndex];
    const originalId = splitOriginalRef.current;
    if (!part || !originalId) return;
    try {
      const result = await authApi.confirmSplit(oppId, originalId, part.geometry, part.area_acres);
      if (result.success) loadData();
    } catch (err) {
      logger.error('Confirm split failed', err, 'opp-map');
    }
  }, [oppId, loadData]);

  const handleMergeRequest = useCallback(async (featureIds: number[]) => {
    if (featureIds.length < 2) return;
    try {
      const result = await authApi.mergeParcels(oppId, featureIds);
      if (result.success) loadData();
    } catch (err) {
      logger.error('Merge parcels failed', err, 'opp-map');
    }
  }, [oppId, loadData]);

  const handleBasemapToggle = useCallback(() => {
    const next = basemap === 'satellite-streets' ? 'outdoors' : 'satellite-streets';
    setBasemap(next);
    postMsg({ type: 'change_basemap', styleUrl: BASEMAP_STYLES[next] });
  }, [basemap, postMsg]);

  if (Platform.OS !== 'web') {
    return (
      <ScreenContainer>
        <ScreenHeader title={opportunityName || 'Map'} onBack={() => router.back()} />
        <View style={styles.center}>
          <Ionicons name="map-outline" size={icon.xxl} color={color.textMuted} />
          <Text style={styles.placeholderText}>Map available on web</Text>
        </View>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <ScreenHeader title={opportunityName || 'Opportunity Map'} onBack={() => router.back()} />

      <View style={styles.container}>
        {!mapboxToken ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color={color.primary} />
          </View>
        ) : (
          <iframe
            ref={iframeRef as any}
            srcDoc={mapHtml}
            style={{ width: '100%', height: '100%', border: 'none' } as any}
            title="Opportunity Map"
          />
        )}

        {/* Basemap toggle floating button */}
        <Pressable style={styles.basemapBtn} onPress={handleBasemapToggle}>
          <Ionicons name={basemap === 'satellite-streets' ? 'globe-outline' : 'ellipse-outline'} size={18} color={color.textPrimary} />
        </Pressable>

        {dataLoading && (
          <View style={styles.loadingBanner}>
            <ActivityIndicator size="small" color={color.surface} />
            <Text style={styles.loadingText}>Loading map data...</Text>
          </View>
        )}
      </View>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative' as any,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.md,
  },
  placeholderText: {
    fontSize: text.body.fontSize,
    color: color.textMuted,
  },
  basemapBtn: {
    position: 'absolute' as any,
    bottom: 20,
    left: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  loadingBanner: {
    position: 'absolute' as any,
    bottom: 20,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
    backgroundColor: 'rgba(0,0,0,0.7)',
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    borderRadius: radius.md,
  },
  loadingText: {
    fontSize: text.caption.fontSize,
    color: '#ffffff',
  },
});
